"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const facilities = [{
  id: 1,
  title: "Premium Stables",
  description: "State-of-the-art equine facilities with spacious stalls and modern amenities",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542870/img/equicanine_premium_stables_facility.png"
}, {
  id: 2,
  title: "Secure Kennels",
  description: "Climate-controlled dog boarding with individual play areas",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542870/img/equicanine_secure_kennels_facility.png"
}, {
  id: 3,
  title: "Exercise Areas",
  description: "Expansive outdoor spaces for training and recreation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542870/img/equicanine_exercise_areas_facility.png"
}];
export default function FacilitiesShowcase() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  useEffect(() => {
    if (!api) return;
    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };
    api.on("select", onSelect);
    onSelect();
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);
  return <section id="facilities-showcase" ref={ref} className={`relative py-16 md:py-24 bg-background transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Our Facilities
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore our world-class facilities designed for the comfort and care of your horses and dogs
          </p>
        </div>

        <div className="relative">
          <Carousel setApi={setApi} opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent>
              {facilities.map((facility, i) => <CarouselItem key={facility.id} data-index={i}>
                  <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-lg">
                    <div className={`absolute inset-0 bg-cover bg-center transition-transform duration-[8000ms] ease-out ${current === i ? "scale-110" : "scale-100"}`} style={{
                  backgroundImage: `url(${facility.image})`
                }} />
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 via-primary-dark/40 to-transparent" />

                    <div className={`absolute bottom-0 left-0 p-6 md:p-10 max-w-2xl transition-all duration-[var(--duration-medium)] ${current === i ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                      <h3 className="text-2xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-3">
                        {facility.title}
                      </h3>
                      <p className="text-base md:text-lg text-white/90">
                        {facility.description}
                      </p>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            <div className={`absolute top-1/2 left-4 -translate-y-1/2 transition-opacity duration-[var(--duration-medium)] ${isHovered ? "opacity-100" : "opacity-0"}`}>
              <Button variant="secondary" size="icon" className="size-12 rounded-full shadow-medium hover:shadow-strong" onClick={() => api?.scrollPrev()} aria-label="Previous facility">
                <ChevronLeft className="size-6" />
              </Button>
            </div>

            <div className={`absolute top-1/2 right-4 -translate-y-1/2 transition-opacity duration-[var(--duration-medium)] ${isHovered ? "opacity-100" : "opacity-0"}`}>
              <Button variant="secondary" size="icon" className="size-12 rounded-full shadow-medium hover:shadow-strong" onClick={() => api?.scrollNext()} aria-label="Next facility">
                <ChevronRight className="size-6" />
              </Button>
            </div>
          </Carousel>

          <div className="flex justify-center gap-2 mt-8">
            {facilities.map((facility, i) => <Button key={facility.id} data-index={i} variant="ghost" size="icon" onClick={() => api?.scrollTo(i)} className={`h-1.5 p-0 rounded-full transition-all duration-[var(--duration-medium)] hover:bg-accent/50 ${current === i ? "w-8 bg-accent" : "w-1.5 bg-muted"}`} aria-label={`Go to ${facility.title}`} />)}
          </div>
        </div>
      </div>
    </section>;
}